import React, { useEffect, useMemo, useState } from 'react';
import {
  Button,
  Grid,
  makeStyles,
  MenuItem,
  TextField,
  Theme,
  Typography,
} from '@material-ui/core';
import GetAppIcon from '@material-ui/icons/GetApp';
import PeopleIcon from '@material-ui/icons/People';
import SchoolIcon from '@material-ui/icons/School';
import ContactsIcon from '@material-ui/icons/Contacts';
import GroupAddIcon from '@material-ui/icons/GroupAdd';
import WorkIcon from '@material-ui/icons/Work';
import AttachMoneyIcon from '@material-ui/icons/AttachMoney';
import CheckCircleOutlineIcon from '@material-ui/icons/CheckCircleOutline';
import LaptopIcon from '@material-ui/icons/Laptop';
import AccountBoxIcon from '@material-ui/icons/AccountBox';
import VpnKeyIcon from '@material-ui/icons/VpnKey';
import AssignmentTurnedInIcon from '@material-ui/icons/AssignmentTurnedIn';
import { Bar, Line } from 'react-chartjs-2';
import {
  format,
  subDays,
  subMonths,
  subQuarters,
  subYears,
  parseISO,
  isAfter,
} from 'date-fns';
import { useHistory } from 'react-router-dom';
import Layout from '../../components/layout/Layout';
import { get, search } from '../../utils/ajax';
import { localRoutes, remoteRoutes } from '../../data/constants';
import { exportToCsv } from '../../utils/exportCsv';
import { formatCompact, APPROX_UGX_PER_USD } from '../../utils/numberHelpers';
import { PRemoteSelect } from '../../components/plain-inputs/PRemoteSelect';
import { IOption } from '../../components/inputs/inputHelpers';
import KpiCard from '../dashboard/components/KpiCard';
import ChartCard from '../dashboard/components/ChartCard';
import SideListCard from '../dashboard/components/SideListCard';
import PaginatedListCard from '../dashboard/components/PaginatedListCard';
import GaugeChart from '../dashboard/components/GaugeChart';
import DonutStat from '../dashboard/components/DonutStat';
import { fontFamilies } from '../../theme/fonts';
import { colors } from '../../theme/tokens';

const CORAL = colors.brandStrong;
const ORANGE = colors.accentOrange;
const GREEN = colors.accentGreen;
const BLUE = colors.accentBlue;
const AMBER = colors.accentAmber;
const PURPLE = colors.accentPurple;
const DARK = colors.textPrimary;

const CHART_PALETTE = [
  CORAL,
  BLUE,
  AMBER,
  GREEN,
  PURPLE,
  '#06b6d4',
  ORANGE,
  '#ec4899',
];

const PLACEMENT_STATUS: Record<string, { label: string; color: string }> = {
  Employed: { label: 'Employed', color: GREEN },
  SelfEmployed: { label: 'Self-Employed', color: BLUE },
  Freelance: { label: 'Freelancing', color: PURPLE },
  Internship: { label: 'Internship', color: AMBER },
};

const DATE_RANGES = [
  { label: 'All Time', key: 'all' },
  { label: 'This Year', key: 'year' },
  { label: 'This Quarter', key: 'quarter' },
  { label: 'This Month', key: 'month' },
  { label: 'This Week', key: 'week' },
];

const useStyles = makeStyles((theme: Theme) => ({
  root: { minHeight: '100%' },

  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 24,
    flexWrap: 'wrap' as any,
    gap: 12,
    [theme.breakpoints.down('xs')]: {
      flexDirection: 'column' as any,
      alignItems: 'flex-start',
    },
  },
  title: {
    fontFamily: fontFamilies.display,
    fontSize: 17,
    fontWeight: 700,
    color: DARK,
    [theme.breakpoints.down('xs')]: { fontSize: 17 },
  },
  subtitle: { fontSize: 15, color: colors.textFaint, marginTop: 2 },

  filterSelect: {
    width: 180,
    [theme.breakpoints.down('xs')]: { width: '100%' },
  },
  filterGroup: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    flexWrap: 'wrap' as any,
    [theme.breakpoints.down('xs')]: {
      display: 'grid',
      gridTemplateColumns: 'repeat(2, 1fr)',
      gap: 10,
      width: '100%',
    },
  },

  exportBtn: {
    background: CORAL,
    color: colors.onBrand,
    borderRadius: 8,
    fontWeight: 600,
    textTransform: 'none' as any,
    fontSize: 13,
    padding: '7px 16px',
    '&:hover': { background: '#d42360' },
    [theme.breakpoints.down('xs')]: { width: '100%' },
  },

  chartCard: {
    background: colors.bgSurface,
    borderRadius: 12,
    border: `1px solid ${colors.border}`,
    padding: '18px 20px',
    boxShadow: `0 1px 3px ${colors.shadow}`,
    height: '100%',
    overflowX: 'auto' as any,
    [theme.breakpoints.down('xs')]: { padding: '14px 12px' },
  },
  chartTitle: {
    fontSize: 11,
    fontWeight: 700,
    color: colors.textFaint,
    textTransform: 'uppercase' as any,
    letterSpacing: '0.07em',
    marginBottom: 16,
  },

  sectionLabel: {
    fontSize: 11,
    fontWeight: 700,
    color: colors.textFaint,
    textTransform: 'uppercase' as any,
    letterSpacing: '0.07em',
    marginBottom: 12,
    marginTop: 28,
  },

  // ── Students by Hub — per-hub cards (restored from the pre-redesign
  // admin dashboard design, rebuilt on today's color tokens) ───────────────
  hubGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
    gap: 16,
    marginBottom: 28,
    [theme.breakpoints.down('xs')]: {
      gridTemplateColumns: 'repeat(2, 1fr)',
      gap: 10,
    },
  },
  hubCard: {
    background: colors.bgSurface,
    borderRadius: 12,
    border: `1px solid ${colors.border}`,
    overflow: 'hidden',
    boxShadow: `0 1px 3px ${colors.shadow}`,
    width: '100%',
    boxSizing: 'border-box' as any,
    cursor: 'pointer',
  },
  hubStripe: { height: 6, width: '100%', background: CORAL },
  hubBody: {
    padding: '14px 16px 16px',
    [theme.breakpoints.down('xs')]: { padding: '10px 12px 12px' },
  },
  hubName: {
    fontSize: 13,
    fontWeight: 700,
    color: DARK,
    marginBottom: 10,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  hubTotal: {
    fontSize: 32,
    fontWeight: 800,
    color: DARK,
    lineHeight: 1,
    [theme.breakpoints.down('xs')]: { fontSize: 24 },
  },
  hubTotalLabel: {
    fontSize: 10,
    color: colors.textFaint,
    fontWeight: 600,
    marginTop: 2,
    marginBottom: 12,
  },
  hubRow: { display: 'flex', justifyContent: 'space-between', marginTop: 6 },
  hubStat: { fontSize: 12, fontWeight: 700 },
  hubStatLabel: {
    fontSize: 10,
    color: colors.textFaint,
    fontWeight: 600,
    marginTop: 1,
  },
  hubBar: {
    height: 3,
    borderRadius: 2,
    background: colors.progressTrack,
    marginTop: 12,
    overflow: 'hidden',
  },
  hubBarFill: {
    height: '100%',
    borderRadius: 2,
    transition: 'width 0.6s ease',
  },
  hubViewDetails: {
    fontSize: 10,
    color: CORAL,
    fontWeight: 700,
    whiteSpace: 'nowrap',
    textAlign: 'right' as any,
    marginTop: 6,
  },
  emptyText: {
    fontSize: 13,
    color: colors.textMuted,
    textAlign: 'center' as any,
    padding: '24px 0',
  },
  onlineNowInline: {
    display: 'flex',
    alignItems: 'center',
    gap: 7,
    fontSize: 13,
    fontWeight: 800,
    color: GREEN,
    flexShrink: 0,
  },
  onlineNowDot: {
    width: 8,
    height: 8,
    borderRadius: '50%',
    background: GREEN,
    flexShrink: 0,
    boxShadow: `0 0 0 3px ${colors.tintGreen}`,
  },

  card: {
    background: colors.bgSurface,
    borderRadius: 12,
    border: `1px solid ${colors.border}`,
    padding: '0 20px',
    boxShadow: `0 1px 3px ${colors.shadow}`,
    overflowX: 'auto' as any,
  },
  cardTableInner: {
    minWidth: 420,
  },
  tableRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    padding: '12px 0',
    borderBottom: `1px solid ${colors.border}`,
    '&:last-child': { borderBottom: 'none' },
  },
  rowDot: { width: 8, height: 8, borderRadius: '50%', flexShrink: 0 },
  rowName: { fontSize: 13, fontWeight: 600, color: DARK, flex: 1 },
  rowNum: {
    fontSize: 13,
    fontWeight: 800,
    color: DARK,
    minWidth: 64,
    textAlign: 'right' as any,
  },
  rowGreen: {
    fontSize: 11,
    color: GREEN,
    fontWeight: 600,
    minWidth: 64,
    textAlign: 'right' as any,
  },

  statChipRow: { display: 'flex', gap: 10, marginTop: 14, flexWrap: 'wrap' },
  statChip: {
    flex: 1,
    minWidth: 100,
    background: colors.bgSubtle,
    borderRadius: 10,
    padding: '10px 12px',
    textAlign: 'center' as any,
  },
  statChipNum: {
    fontFamily: fontFamilies.display,
    fontSize: 15,
    fontWeight: 700,
    color: DARK,
  },
  statChipLabel: {
    fontSize: 10,
    color: colors.textFaint,
    fontWeight: 600,
    marginTop: 2,
  },

  viewAllLink: {
    fontSize: 11,
    fontWeight: 700,
    color: CORAL,
    cursor: 'pointer',
    whiteSpace: 'nowrap',
  },

  '@global': {
    '@media print': {
      '.no-print': { display: 'none !important' },
      body: { background: colors.bgSurface },
    },
  },
}));

const cutoffDate = (rangeKey: string): Date | null => {
  const now = new Date();
  if (rangeKey === 'week') return subDays(now, 7);
  if (rangeKey === 'month') return subMonths(now, 1);
  if (rangeKey === 'quarter') return subQuarters(now, 1);
  if (rangeKey === 'year') return subYears(now, 1);
  return null;
};

const barOpts: any = {
  responsive: true,
  maintainAspectRatio: false,
  legend: { display: false },
  scales: {
    yAxes: [
      {
        ticks: { beginAtZero: true, precision: 0, maxTicksLimit: 5 },
        gridLines: {},
      },
    ],
    xAxes: [
      {
        gridLines: { display: false },
        ticks: { fontSize: 11, autoSkip: true, maxTicksLimit: 8 },
      },
    ],
  },
};
const lineOpts: any = {
  responsive: true,
  maintainAspectRatio: false,
  legend: { display: false },
  scales: {
    yAxes: [
      {
        ticks: { beginAtZero: true, precision: 0 },
        gridLines: {},
      },
    ],
    xAxes: [
      {
        gridLines: { display: false },
        ticks: { fontSize: 11, autoSkip: true, maxTicksLimit: 6 },
      },
    ],
  },
};

// One standard size for every chart/donut on this page — matches the
// reference dashboards where every card in a tier shares identical sizing.
const CHART_HEIGHT = 220;

// Fixed-height wrapper so chart.js fills a predictable box instead of
// fighting the card's flexible width via maintainAspectRatio.
// height is a floor, not a cap — the box also fills 100% of its stretched
// card so a chart never leaves dead space below it when a row sibling is taller.
//
// scrollCategoryCount: when a bar chart's category count can grow unbounded
// (many hubs/courses), pass the category count so each bar keeps a healthy
// minimum width and the chart scrolls horizontally instead of squeezing bars
// down to slivers and auto-skipping labels.
const ChartBox: React.FC<{
  height?: number;
  children: React.ReactNode;
  scrollCategoryCount?: number;
}> = ({ height = CHART_HEIGHT, children, scrollCategoryCount }) => {
  const scrollMinWidth = scrollCategoryCount
    ? Math.max(scrollCategoryCount * 90, 1)
    : undefined;
  return (
    <div
      style={{
        height: '100%',
        minHeight: height,
        position: 'relative',
        overflowX: scrollMinWidth ? 'auto' : undefined,
      }}
    >
      <div style={{ minWidth: scrollMinWidth, height: '100%' }}>{children}</div>
    </div>
  );
};

const ReportList: React.FC = () => {
  const classes = useStyles();
  const history = useHistory();

  const [allStudents, setAllStudents] = useState<any[]>([]);
  const [onlineNowCount, setOnlineNowCount] = useState<number | null>(null);

  useEffect(() => {
    const fetchOnlineCount = () => {
      get(remoteRoutes.presenceOnlineCount, (data: any) => {
        setOnlineNowCount(typeof data?.count === 'number' ? data.count : null);
      });
    };
    fetchOnlineCount();
    const interval = setInterval(fetchOnlineCount, 45 * 1000);
    return () => clearInterval(interval);
  }, []);

  const [dateRange, setDateRange] = useState('all');
  const [hub, setHub] = useState<IOption | null>(null);
  const [course, setCourse] = useState<IOption | null>(null);
  const [attendanceSessions, setAttendanceSessions] = useState<any[]>([]);
  const [courseCheckins, setCourseCheckins] = useState<any[]>([]);
  const [allCourses, setAllCourses] = useState<any[]>([]);
  const [allHubs, setAllHubs] = useState<any[]>([]);
  const [allPlacements, setAllPlacements] = useState<any[]>([]);
  const [stats, setStats] = useState<any>(null);
  const [hubStatsData, setHubStatsData] = useState<any[]>([]);
  const [communityReachTotal, setCommunityReachTotal] = useState(0);
  const [topPerformersData, setTopPerformersData] = useState<{
    topStudents: any[];
  }>({ topStudents: [] });

  useEffect(() => {
    search(
      remoteRoutes.students,
      { limit: 10000, skip: 0 },
      (data: any) =>
        setAllStudents(data?.data || (Array.isArray(data) ? data : [])),
      undefined,
      undefined,
    );
    search(
      remoteRoutes.attendanceSessions,
      { limit: 2000 },
      (data: any) => {
        const list = Array.isArray(data)
          ? data
          : data?.sessions || data?.data || [];
        setAttendanceSessions(list);
      },
      undefined,
      undefined,
    );
    search(
      remoteRoutes.courses,
      {},
      (data: any) =>
        setAllCourses(Array.isArray(data) ? data : data?.data || []),
      undefined,
      undefined,
    );
    search(
      remoteRoutes.courseCheckins,
      {},
      (data: any) =>
        setCourseCheckins(Array.isArray(data) ? data : data?.data || []),
      undefined,
      undefined,
    );
    search(
      remoteRoutes.hubs,
      {},
      (data: any) => setAllHubs(Array.isArray(data) ? data : data?.data || []),
      undefined,
      undefined,
    );
    search(
      remoteRoutes.jobPlacements,
      {},
      (data: any) =>
        setAllPlacements(Array.isArray(data) ? data : data?.data || []),
      undefined,
      undefined,
    );
    search(
      remoteRoutes.dashboardStats,
      {},
      (data: any) => setStats(data),
      undefined,
      undefined,
    );
    search(
      remoteRoutes.hubStats,
      {},
      (data: any) =>
        setHubStatsData(Array.isArray(data) ? data : data?.data || []),
      undefined,
      undefined,
    );
    search(
      `${remoteRoutes.communityReach}/stats`,
      {},
      (data: any) => setCommunityReachTotal(data?.total || 0),
      undefined,
      undefined,
    );
  }, []);

  // Top performers — the only one of these that can be scoped by hub, so it
  // refetches whenever the hub filter changes (course-level scoping isn't
  // supported by this endpoint).
  useEffect(() => {
    search(
      remoteRoutes.dashboardTopPerformers,
      { hubId: hub?.id, limit: 10 },
      (data: any) =>
        setTopPerformersData({
          topStudents: (data?.topStudents || []).slice(0, 10),
        }),
      undefined,
      undefined,
    );
  }, [hub]);

  // ── Filter students by selected date range + hub/course ───────────────────
  const filteredStudents = useMemo(() => {
    const cutoff = cutoffDate(dateRange);
    return allStudents.filter((s) => {
      if (cutoff) {
        if (!s.registeredAt || !isAfter(parseISO(s.registeredAt), cutoff))
          return false;
      }
      if (hub && s.hubName !== hub.name) return false;
      if (course && s.courseName !== course.name) return false;
      return true;
    });
  }, [allStudents, dateRange, hub, course]);

  const total = filteredStudents.length;
  // People who signed up but never enrolled, so they have no student row.
  // They were still reached, so they belong in Total Youth Reached — the
  // admin dashboard counts them and this screen did not, which is why the
  // two disagreed.
  const registeredNotEnrolled = stats?.registeredNotEnrolled ?? 0;
  const totalYouthReached = communityReachTotal + total + registeredNotEnrolled;
  const active = filteredStudents.filter(
    (s) => (s.status || '').toLowerCase() === 'active',
  ).length;

  const hubBreakdown = useMemo(() => {
    const map: Record<
      string,
      {
        hub: string;
        name: string;
        total: number;
        active: number;
        completed: number;
      }
    > = {};
    filteredStudents.forEach((s) => {
      const key = s.hub || 'unknown';
      if (!map[key])
        map[key] = {
          hub: key,
          name: s.hubName || key,
          total: 0,
          active: 0,
          completed: 0,
        };
      map[key].total++;
      const st = (s.status || '').toLowerCase();
      if (st === 'active') map[key].active++;
      if (st === 'completed') map[key].completed++;
    });
    return Object.values(map).sort((a, b) => b.total - a.total);
  }, [filteredStudents]);

  const courseBreakdown = useMemo(() => {
    const map: Record<
      string,
      { course: string; total: number; active: number; completed: number }
    > = {};
    filteredStudents.forEach((s) => {
      const key = s.courseName || 'Uncategorised';
      if (!map[key])
        map[key] = { course: key, total: 0, active: 0, completed: 0 };
      map[key].total++;
      const st = (s.status || '').toLowerCase();
      if (st === 'active') map[key].active++;
      if (st === 'completed') map[key].completed++;
    });
    return Object.values(map).sort((a, b) => b.total - a.total);
  }, [filteredStudents]);

  const growthData = useMemo(() => {
    const map: Record<string, number> = {};
    for (let i = 11; i >= 0; i--)
      map[format(subMonths(new Date(), i), 'MMM yy')] = 0;
    filteredStudents.forEach((s) => {
      if (!s.registeredAt) return;
      try {
        const key = format(parseISO(s.registeredAt), 'MMM yy');
        if (key in map) map[key] = (map[key] || 0) + 1;
      } catch {}
    });
    const labels = Object.keys(map);
    return {
      labels,
      datasets: [
        {
          label: 'New Registrations',
          data: labels.map((l) => map[l]),
          borderColor: CORAL,
          backgroundColor: colors.brandTint,
          fill: true,
          tension: 0.4,
          pointRadius: 4,
          pointBackgroundColor: CORAL,
        },
      ],
    };
  }, [filteredStudents]);

  const STATUS_LABELS: Record<string, string> = {
    active: 'Active',
    completed: 'Completed',
    graduated: 'Graduated',
    onbreak: 'On Break',
    dropped: 'Dropped',
  };
  const STATUS_COLORS: Record<string, string> = {
    active: GREEN,
    completed: BLUE,
    graduated: PURPLE,
    onbreak: AMBER,
    dropped: CORAL,
  };
  const statusData = useMemo(() => {
    const map: Record<string, number> = {};
    filteredStudents.forEach((s) => {
      const raw = (s.status || '').toLowerCase();
      const key = STATUS_LABELS[raw] ? raw : 'active';
      map[key] = (map[key] || 0) + 1;
    });
    return Object.keys(map).map((k) => ({
      label: STATUS_LABELS[k] || k,
      value: map[k],
      color: STATUS_COLORS[k] || '#94a3b8',
    }));
  }, [filteredStudents]);

  // ── Trainers directory: name → courses they teach ─────────────────────────
  const trainerDirectory = useMemo(() => {
    const map: Record<string, string[]> = {};
    allCourses.forEach((c) => {
      if (!c.instructor) return;
      if (!map[c.instructor]) map[c.instructor] = [];
      map[c.instructor].push(c.title || c.name);
    });
    return Object.keys(map)
      .sort()
      .map((name) => ({ name, courses: map[name] }));
  }, [allCourses]);

  // ── Hub managers directory: name → hub they manage ────────────────────────
  const hubManagerDirectory = useMemo(
    () =>
      allHubs
        .filter((h) => h.managerName)
        .map((h) => ({ name: h.managerName, hub: h.name }))
        .sort((a, b) => a.name.localeCompare(b.name)),
    [allHubs],
  );

  const attendanceChartData = useMemo(() => {
    const days: { label: string; date: string }[] = [];
    for (let i = 6; i >= 0; i--) {
      const d = subDays(new Date(), i);
      days.push({ label: format(d, 'EEE'), date: format(d, 'yyyy-MM-dd') });
    }
    const countByDate: Record<string, number> = {};
    days.forEach((d) => (countByDate[d.date] = 0));
    attendanceSessions.forEach((s) => {
      const raw = s.date || s.sessionDate || s.createdAt || s.startTime || '';
      const dateStr = raw ? raw.slice(0, 10) : '';
      if (dateStr in countByDate) {
        const n =
          s._count?.records ??
          s.attendanceCount ??
          s.checkinCount ??
          s.count ??
          0;
        countByDate[dateStr] += Number(n);
      }
    });
    return {
      labels: days.map((d) => d.label),
      datasets: [
        {
          label: 'Check-ins',
          data: days.map((d) => countByDate[d.date]),
          borderColor: CORAL,
          backgroundColor: colors.brandTint,
          pointBackgroundColor: CORAL,
          pointRadius: 4,
          pointHoverRadius: 6,
          borderWidth: 2,
          fill: true,
          tension: 0.4,
        },
      ],
    };
  }, [attendanceSessions]);

  const attendanceByCourse = useMemo(() => {
    const map: Record<string, number> = {};
    attendanceSessions.forEach((s) => {
      const key =
        s.course?.title || s.event?.title || s.courseName || 'Uncategorised';
      const n =
        s._count?.records ??
        s.attendanceCount ??
        s.checkinCount ??
        s.count ??
        0;
      map[key] = (map[key] || 0) + Number(n);
    });
    return Object.entries(map)
      .map(([course, total]) => ({ course, total }))
      .sort((a, b) => b.total - a.total);
  }, [attendanceSessions]);

  // Independent from the QR/session attendance chart above — daily counts of
  // self-paced online course check-ins (course_checkin), not related data.
  const onlineCourseAttendanceChartData = useMemo(() => {
    const days: { label: string; date: string }[] = [];
    for (let i = 6; i >= 0; i--) {
      const d = subDays(new Date(), i);
      days.push({ label: format(d, 'EEE'), date: format(d, 'yyyy-MM-dd') });
    }
    const countByDate: Record<string, number> = {};
    days.forEach((d) => (countByDate[d.date] = 0));
    courseCheckins.forEach((c) => {
      const dateStr = c.checkedInAt ? String(c.checkedInAt).slice(0, 10) : '';
      if (dateStr in countByDate) countByDate[dateStr] += 1;
    });
    return {
      labels: days.map((d) => d.label),
      datasets: [
        {
          label: 'Online Check-ins',
          data: days.map((d) => countByDate[d.date]),
          borderColor: BLUE,
          backgroundColor: colors.tintBlue,
          pointBackgroundColor: BLUE,
          pointRadius: 4,
          pointHoverRadius: 6,
          borderWidth: 2,
          fill: true,
          tension: 0.4,
        },
      ],
    };
  }, [courseCheckins]);

  // ── Job placements scoped to the selected hub/course ──────────────────────
  const scopedPlacements = useMemo(() => {
    // Placements honour the date range the same way students do. They used
    // to ignore it, so changing the range moved every other figure on the
    // page while Job Placements and the wages total sat still.
    const cutoff = cutoffDate(dateRange);
    return allPlacements.filter((p) => {
      if (hub && String(p.hubId) !== String(hub.id)) return false;
      if (course && String(p.courseId) !== String(course.id)) return false;
      if (cutoff) {
        if (!p.createdAt || !isAfter(parseISO(p.createdAt), cutoff))
          return false;
      }
      return true;
    });
  }, [allPlacements, hub, course, dateRange]);

  const placementsByType = useMemo(() => {
    const map: Record<string, number> = {};
    scopedPlacements.forEach((p) => {
      map[p.placementType] = (map[p.placementType] || 0) + 1;
    });
    return map;
  }, [scopedPlacements]);

  const placementsGenderBreakdown = useMemo(() => {
    const map: Record<string, number> = {};
    scopedPlacements.forEach((p) => {
      if (p.gender) map[p.gender] = (map[p.gender] || 0) + 1;
    });
    return Object.keys(map).map((g) => ({ gender: g, count: map[g] }));
  }, [scopedPlacements]);

  const recruitedForWork = useMemo(
    () =>
      scopedPlacements
        .filter((p) => p.jobTitle || p.companyName)
        .sort((a, b) => (b.id ?? 0) - (a.id ?? 0))
        .map((p) => ({
          name: p.fullName,
          jobTitle: p.jobTitle,
          companyName: p.companyName,
          course: p.course?.title,
          hub: p.hub?.name,
          placementType: p.placementType,
        })),
    [scopedPlacements],
  );

  // ── Completion / graduation — derived from each student's lifecycle status
  const completedGraduated = filteredStudents.filter((s) =>
    ['completed', 'graduated'].includes((s.status || '').toLowerCase()),
  ).length;
  const graduatedCount = filteredStudents.filter(
    (s) => (s.status || '').toLowerCase() === 'graduated',
  ).length;
  const completedCount = filteredStudents.filter(
    (s) => (s.status || '').toLowerCase() === 'completed',
  ).length;
  const completionRate =
    total > 0 ? Math.round((completedGraduated / total) * 100) : 0;

  // ── Active / inactive lists — derived from lifecycle status (the
  // ≥2-days-in-30-days check-in definition needs a dedicated endpoint we're
  // no longer calling, so this uses the student's own status instead).
  const activeStatusStudents = useMemo(
    () =>
      filteredStudents
        .filter((s) => (s.status || '').toLowerCase() === 'active')
        .map((s) => ({ name: s.name, hub: s.hubName, course: s.courseName })),
    [filteredStudents],
  );
  const inactiveStatusStudents = useMemo(
    () =>
      filteredStudents
        .filter((s) =>
          ['onbreak', 'dropped'].includes((s.status || '').toLowerCase()),
        )
        .map((s) => ({ name: s.name, hub: s.hubName, course: s.courseName })),
    [filteredStudents],
  );

  const handleExport = () => {
    const headers = ['Metric', 'Value'];
    const rows: (string | number)[][] = [
      ['Total Students', total],
      ['Active Students (checked in 2+ days in last 30)', activeStudentsCount],
      ['Students (status: Active)', active],
      ['Students (status: Graduated)', graduatedCount],
      ['Students (status: Completed)', completedCount],
      ['Community Reach', communityReachTotal],
      ['Total Youth Reached', totalYouthReached],
      ['Total Job Placements', scopedPlacements.length],
      [`Earned Income by Graduates (UGX) — ${wagesPeriodLabel}`, earnedIncome],
    ];
    hubBreakdown.forEach((h) => rows.push([`Hub — ${h.name}`, h.total]));
    courseBreakdown.forEach((c) =>
      rows.push([`Course — ${c.course}`, c.total]),
    );
    const scope = [hub?.name, course?.name].filter(Boolean).join('-') || 'all';
    exportToCsv(`reports-analytics-${scope}.csv`, headers, rows);
  };

  const wagesPeriodLabel =
    new Date().getMonth() === 0
      ? format(new Date(), 'MMM yyyy')
      : `${format(new Date(new Date().getFullYear(), 0, 1), 'MMM')}–${format(
          new Date(),
          'MMM yyyy',
        )}`;

  // Earned Income by Graduates — same calculation as the Job Placements
  // banner: every graduate earning a monthly salary (Employed/Self-Employed/
  // Freelance) or a paid internship stipend contributes their income for
  // each month elapsed since January, so the total climbs on its own as the
  // year goes on.
  const monthsElapsed = new Date().getMonth() + 1;
  const earnedIncome = useMemo(
    () =>
      scopedPlacements.reduce((sum, p) => {
        if (p.placementType === 'Internship') {
          return (
            sum + (p.isPaidInternship ? Number(p.internshipStipend) || 0 : 0)
          );
        }
        return sum + (Number(p.salaryAfterProgram) || 0);
      }, 0) * monthsElapsed,
    [scopedPlacements, monthsElapsed],
  );
  const earnedIncomeUsd = Math.round(earnedIncome / APPROX_UGX_PER_USD);

  // Active Students KPI — always the attendance-based definition (2+ days
  // of activity in the last 30 days), scoped to the selected hub when
  // hub-stats has a matching entry, otherwise the org-wide figure. It
  // previously fell back to a roster-status count (activeCount) when a hub
  // was selected, which was a different metric entirely.
  const matchingHubStat = hub
    ? hubStatsData.find((h) => h.hub === hub.name)
    : null;
  const activeStudentsCount =
    matchingHubStat?.activeStudents ?? stats?.activeStudents ?? '—';
  const enrolledCount = stats?.activeEnrollments ?? '—';

  return (
    <Layout
      title="Reports & Analytics"
      subtitle={`Programme performance overview · ${format(
        new Date(),
        'MMMM d, yyyy',
      )}`}
    >
      <div className={classes.root}>
        {/* ── Header ────────────────────────────────────────────────────── */}
        <div className={classes.header}>
          <div className={classes.onlineNowInline}>
            <span className={classes.onlineNowDot} />
            {onlineNowCount ?? 0} student
            {onlineNowCount === 1 ? '' : 's'} active now
          </div>
          <div className={`${classes.filterGroup} no-print`}>
            <div className={classes.filterSelect}>
              <PRemoteSelect
                remote={remoteRoutes.hubsCombo}
                name="hub"
                label="Filter by hub"
                variant="outlined"
                size="small"
                margin="none"
                value={hub}
                onChange={(value) => setHub(value as IOption | null)}
              />
            </div>
            <div className={classes.filterSelect}>
              <PRemoteSelect
                remote={remoteRoutes.coursesCombo}
                name="course"
                label="Filter by course"
                variant="outlined"
                size="small"
                margin="none"
                value={course}
                onChange={(value) => setCourse(value as IOption | null)}
              />
            </div>
            <TextField
              select
              label="Date Range"
              variant="outlined"
              size="small"
              className={classes.filterSelect}
              value={dateRange}
              onChange={(e) => setDateRange(e.target.value)}
            >
              {DATE_RANGES.map((r) => (
                <MenuItem key={r.key} value={r.key}>
                  {r.label}
                </MenuItem>
              ))}
            </TextField>
            <Button
              className={classes.exportBtn}
              startIcon={<GetAppIcon style={{ fontSize: 16 }} />}
              onClick={handleExport}
            >
              Export CSV
            </Button>
          </div>
        </div>

        {/* ── Students by Hub ───────────────────────────────────────────── */}
        <div className={classes.sectionLabel} style={{ marginTop: 0 }}>
          Students by Hub
        </div>
        {hubStatsData.length === 0 ? (
          <Typography className={classes.emptyText}>No hub data yet</Typography>
        ) : (
          <div className={classes.hubGrid}>
            {hubStatsData.map((h: any) => {
              const ai = h.attendanceInfo;
              const displayCount = ai?.presentCount ?? h.presentToday;
              // "On site" only means anything if the register is today's —
              // when the newest register is older, the number is historical,
              // so it's labelled as such instead of looking like today's.
              const staleAttendance = !!ai && !ai.isToday;
              const presentRate =
                h.studentCount > 0
                  ? Math.round((displayCount / h.studentCount) * 100)
                  : 0;
              return (
                <div
                  key={h.hub}
                  className={classes.hubCard}
                  onClick={() => history.push(localRoutes.hubs)}
                >
                  <div className={classes.hubStripe} />
                  <div className={classes.hubBody}>
                    <div className={classes.hubName}>{h.hub}</div>
                    <div className={classes.hubTotal}>{h.studentCount}</div>
                    <div className={classes.hubTotalLabel}>Total Students</div>
                    <div className={classes.hubRow}>
                      <div>
                        <div
                          className={classes.hubStat}
                          style={{ color: staleAttendance ? AMBER : GREEN }}
                        >
                          {displayCount}
                        </div>
                        <div className={classes.hubStatLabel}>
                          {staleAttendance ? `${ai.daysAgo}d ago` : 'On site'}
                        </div>
                      </div>
                      <div style={{ textAlign: 'right' }}>
                        <div
                          className={classes.hubStat}
                          style={{ color: GREEN }}
                        >
                          {h.loggedInToday}
                        </div>
                        <div
                          className={classes.hubStatLabel}
                          style={{ color: GREEN }}
                        >
                          Online today
                        </div>
                      </div>
                    </div>
                    <div className={classes.hubBar}>
                      <div
                        className={classes.hubBarFill}
                        style={{
                          width: `${presentRate}%`,
                          background: staleAttendance ? AMBER : GREEN,
                        }}
                      />
                    </div>
                    <div className={classes.hubViewDetails}>View Details</div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* ── KPI row ───────────────────────────────────────────────────── */}
        {(() => {
          const onlineCheckinInfo = stats?.onlineCheckinInfo;
          const onlineCheckinCount = onlineCheckinInfo?.presentCount ?? 0;
          let onlineCheckinSub = 'Self-paced course player';
          let onlineCheckinSubColor = colors.textMuted;
          if (onlineCheckinInfo && !onlineCheckinInfo.isToday) {
            const d = new Date(onlineCheckinInfo.date + 'T00:00:00');
            const mon = d.toLocaleString('default', { month: 'short' });
            const day = d.getDate();
            const when =
              onlineCheckinInfo.daysAgo === 1
                ? 'yesterday'
                : `${onlineCheckinInfo.daysAgo}d ago`;
            onlineCheckinSub = `${when} · ${mon} ${day}`;
            onlineCheckinSubColor = AMBER;
          }

          return (
            <Grid container spacing={2} alignItems="stretch">
              {[
                {
                  label: 'Total Students',
                  value: total,
                  Icon: PeopleIcon,
                  color: CORAL,
                  bg: colors.brandTint,
                },
                {
                  label: 'Enrolled',
                  value: enrolledCount,
                  Icon: SchoolIcon,
                  color: GREEN,
                  bg: colors.tintGreen,
                },
                {
                  label: 'Active Students',
                  value: activeStudentsCount,
                  Icon: CheckCircleOutlineIcon,
                  color: GREEN,
                  bg: colors.tintGreen,
                  sub: 'Active on ≥2 days in last 30 days',
                  subColor: GREEN,
                },
                {
                  label: 'Community Reach',
                  value: communityReachTotal,
                  Icon: ContactsIcon,
                  color: BLUE,
                  bg: colors.tintBlue,
                  sub: 'People reached via outreach',
                  subColor: colors.textMuted,
                },
                {
                  label: 'Total Youth Reached',
                  value: totalYouthReached,
                  Icon: GroupAddIcon,
                  color: PURPLE,
                  bg: colors.tintPurple,
                  sub: 'Outreach + enrolled students',
                  subColor: PURPLE,
                },
                {
                  label: 'Job Placements',
                  value: scopedPlacements.length,
                  Icon: WorkIcon,
                  color: AMBER,
                  bg: colors.tintAmber,
                },
                {
                  label: `≈ $${formatCompact(
                    earnedIncomeUsd,
                  )} · Wages Earned (This Year)`,
                  value: `UGX ${formatCompact(earnedIncome)}`,
                  Icon: AttachMoneyIcon,
                  color: GREEN,
                  bg: colors.tintGreen,
                  sub: `Earned by Graduates · ${wagesPeriodLabel}`,
                  subColor: GREEN,
                },
                {
                  label: 'Hubs',
                  value: hubBreakdown.length,
                  Icon: SchoolIcon,
                  color: DARK,
                  bg: colors.bgRaised,
                },
                {
                  label: 'Total Online Check-ins',
                  value: onlineCheckinCount,
                  Icon: LaptopIcon,
                  color: BLUE,
                  bg: colors.tintBlue,
                  sub: onlineCheckinSub,
                  subColor: onlineCheckinSubColor,
                },
                {
                  label: 'Blueprints Completed',
                  value: stats?.blueprintsComplete ?? 0,
                  Icon: AccountBoxIcon,
                  color: AMBER,
                  bg: colors.tintAmber,
                  sub: 'Student portfolios ready to share',
                  subColor: colors.textMuted,
                },
                {
                  label: 'Logins Today',
                  value: stats?.loggedInTodayTotal ?? 0,
                  Icon: VpnKeyIcon,
                  color: GREEN,
                  bg: colors.tintGreen,
                },
                {
                  label: 'Assignment Submissions',
                  value: stats?.totalSubmissions ?? 0,
                  Icon: AssignmentTurnedInIcon,
                  color: PURPLE,
                  bg: colors.tintPurple,
                },
              ].map(({ label, value, Icon, color, bg, sub, subColor }) => (
                <Grid
                  item
                  xs={6}
                  sm={4}
                  md={3}
                  key={label}
                  style={{ display: 'flex' }}
                >
                  <KpiCard
                    icon={Icon}
                    value={value}
                    label={label}
                    color={color}
                    bg={bg}
                    sub={sub}
                    subColor={subColor}
                  />
                </Grid>
              ))}
            </Grid>
          );
        })()}

        {/* ── Attendance ────────────────────────────────────────────────── */}
        <div className={classes.sectionLabel}>Attendance</div>
        <Grid container spacing={2} alignItems="stretch">
          <Grid item xs={12} md={7}>
            <ChartCard title="Past 7 Days — Daily Check-ins (all hubs)">
              <ChartBox>
                <Line data={attendanceChartData} options={lineOpts} />
              </ChartBox>
            </ChartCard>
          </Grid>
          <Grid item xs={12} md={5}>
            <ChartCard title="Online Course Attendance — Past 7 Days">
              <ChartBox>
                <Line
                  data={onlineCourseAttendanceChartData}
                  options={lineOpts}
                />
              </ChartBox>
            </ChartCard>
          </Grid>
        </Grid>

        {/* ── Inactive students ─────────────────────────────────────────── */}
        <div className={classes.sectionLabel}>
          Students by Hub — Active / Inactive
        </div>
        <Grid container spacing={2} alignItems="stretch">
          <Grid item xs={12} md={5}>
            <PaginatedListCard
              title="Inactive Students (status: On Break / Dropped)"
              items={inactiveStatusStudents}
              pageSize={8}
              emptyText="No inactive students — everyone is Active"
              renderItem={(s: any) => (
                <div>
                  <div style={{ fontSize: 12, fontWeight: 700, color: DARK }}>
                    {s.name}
                  </div>
                  <div
                    style={{
                      fontSize: 10,
                      color: colors.textFaint,
                      marginTop: 2,
                    }}
                  >
                    {s.hub} · {s.course || '—'}
                  </div>
                </div>
              )}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <PaginatedListCard
              title="Total Attendance by Course"
              items={attendanceByCourse}
              pageSize={8}
              emptyText="No attendance data"
              renderItem={(c: any) => (
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                  }}
                >
                  <span style={{ fontSize: 12, fontWeight: 700, color: DARK }}>
                    {c.course}
                  </span>
                  <span style={{ fontSize: 13, fontWeight: 700, color: CORAL }}>
                    {c.total}
                  </span>
                </div>
              )}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <ChartCard title="Graduation Rate">
              <GaugeChart
                value={completionRate}
                label="Completed or Graduated"
                color={CORAL}
                height={140}
              />
              <div className={classes.statChipRow}>
                <div className={classes.statChip}>
                  <div className={classes.statChipNum}>{graduatedCount}</div>
                  <div className={classes.statChipLabel}>Graduated</div>
                </div>
                <div className={classes.statChip}>
                  <div className={classes.statChipNum}>{completedCount}</div>
                  <div className={classes.statChipLabel}>Completed</div>
                </div>
              </div>
            </ChartCard>
          </Grid>
        </Grid>

        {/* ── Enrollment growth + status donut ──────────────────────────── */}
        <div className={classes.sectionLabel}>Enrollment & Completion</div>
        <Grid container spacing={2} alignItems="stretch">
          <Grid item xs={12} md={7}>
            <ChartCard title="New Registrations — Last 12 Months">
              <ChartBox>
                <Line data={growthData} options={lineOpts} />
              </ChartBox>
            </ChartCard>
          </Grid>
          <Grid item xs={12} md={5}>
            <ChartCard title="Students by Status">
              <DonutStat items={statusData} />
            </ChartCard>
          </Grid>
        </Grid>

        {/* ── Performance ───────────────────────────────────────────────── */}
        <div className={classes.sectionLabel}>Performance</div>
        <Grid container spacing={2} alignItems="stretch">
          <Grid item xs={12}>
            <SideListCard
              title="Top Performers"
              items={topPerformersData.topStudents.slice(0, 4)}
              pageSize={4}
              emptyText="No graded submissions yet"
              headerRight={
                <span
                  className={classes.viewAllLink}
                  onClick={() => history.push(localRoutes.performance)}
                >
                  View All →
                </span>
              }
              renderItem={(s: any, i: number) => (
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <span
                    style={{
                      fontSize: 11,
                      fontWeight: 700,
                      color: colors.textFaint,
                      minWidth: 16,
                    }}
                  >
                    {i + 1}.
                  </span>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div
                      style={{
                        fontSize: 12,
                        fontWeight: 700,
                        color: DARK,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {s.name}
                    </div>
                    <div style={{ fontSize: 10, color: colors.textFaint }}>
                      {s.courseName}
                    </div>
                  </div>
                  <span style={{ fontSize: 13, fontWeight: 800, color: CORAL }}>
                    {s.avgGrade}%
                  </span>
                </div>
              )}
            />
          </Grid>
        </Grid>

        {/* ── Job placements ────────────────────────────────────────────── */}
        <div className={classes.sectionLabel}>Job Placements</div>
        <Grid container spacing={2} alignItems="stretch">
          <Grid item xs={12} md={4}>
            <ChartCard
              title="By Placement Type"
              isEmpty={scopedPlacements.length === 0}
            >
              {scopedPlacements.length > 0 && (
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    height: '100%',
                    justifyContent: 'center',
                  }}
                >
                  <DonutStat
                    fillHeight={false}
                    items={Object.entries(placementsByType).map(
                      ([type, count]) => ({
                        label: PLACEMENT_STATUS[type]?.label || type,
                        value: count as number,
                        color:
                          PLACEMENT_STATUS[type]?.color || colors.textFaint,
                      }),
                    )}
                  />
                  {placementsGenderBreakdown.length > 0 && (
                    <div
                      style={{
                        marginTop: 16,
                        paddingTop: 12,
                        borderTop: `1px solid ${colors.border}`,
                        display: 'flex',
                        gap: 16,
                        flexWrap: 'wrap',
                      }}
                    >
                      {placementsGenderBreakdown.map((g) => (
                        <div
                          key={g.gender}
                          style={{
                            fontSize: 11,
                            color: colors.textMuted,
                            fontWeight: 600,
                          }}
                        >
                          {g.gender}:{' '}
                          <span style={{ color: DARK, fontWeight: 800 }}>
                            {g.count}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </ChartCard>
          </Grid>
          <Grid item xs={12} md={8}>
            <SideListCard
              title="Recently Employed"
              items={recruitedForWork.slice(0, 4)}
              pageSize={4}
              emptyText="No placements with job details yet"
              headerRight={
                <span
                  className={classes.viewAllLink}
                  onClick={() => history.push(localRoutes.jobPlacements)}
                >
                  View All →
                </span>
              }
              renderItem={(p: any) => {
                const status = PLACEMENT_STATUS[p.placementType] || {
                  label: p.placementType || '—',
                  color: colors.textMuted,
                };
                return (
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      gap: 8,
                    }}
                  >
                    <div style={{ minWidth: 0 }}>
                      <div
                        style={{
                          fontSize: 12,
                          fontWeight: 700,
                          color: DARK,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {p.name}
                      </div>
                      <div style={{ fontSize: 10, color: colors.textFaint }}>
                        {p.jobTitle || p.companyName || p.course} · {p.hub}
                      </div>
                    </div>
                    <span
                      style={{
                        fontSize: 11,
                        fontWeight: 700,
                        color: status.color,
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {status.label}
                    </span>
                  </div>
                );
              }}
            />
          </Grid>
        </Grid>

        {/* ── Team directory ────────────────────────────────────────────── */}
        <div className={classes.sectionLabel}>Team Directory</div>
        <Grid container spacing={2} alignItems="stretch">
          <Grid item xs={12} md={6}>
            <PaginatedListCard
              title={`Trainers (${trainerDirectory.length})`}
              items={trainerDirectory}
              pageSize={6}
              emptyText="No trainers assigned to courses yet"
              renderItem={(t: any) => (
                <div>
                  <div style={{ fontSize: 12, fontWeight: 700, color: DARK }}>
                    {t.name}
                  </div>
                  <div
                    style={{
                      fontSize: 10,
                      color: colors.textFaint,
                      marginTop: 2,
                    }}
                  >
                    {t.courses.join(', ')}
                  </div>
                </div>
              )}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <PaginatedListCard
              title={`Hub Managers (${hubManagerDirectory.length})`}
              items={hubManagerDirectory}
              pageSize={6}
              emptyText="No hub managers assigned yet"
              renderItem={(m: any) => (
                <div>
                  <div style={{ fontSize: 12, fontWeight: 700, color: DARK }}>
                    {m.name}
                  </div>
                  <div
                    style={{
                      fontSize: 10,
                      color: colors.textFaint,
                      marginTop: 2,
                    }}
                  >
                    {m.hub}
                  </div>
                </div>
              )}
            />
          </Grid>
        </Grid>

        {/* ── Hub Comparison ────────────────────────────────────────────── */}
        <div className={classes.sectionLabel}>Hub Comparison</div>
        <Grid container spacing={2} alignItems="stretch">
          <Grid item xs={12} md={8}>
            <div
              className={classes.card}
              style={{ height: '100%', boxSizing: 'border-box' }}
            >
              <div className={classes.cardTableInner}>
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 10,
                    padding: '10px 0 6px',
                    borderBottom: `2px solid ${colors.border}`,
                  }}
                >
                  <span style={{ width: 8, flexShrink: 0 }} />
                  <span
                    style={{
                      flex: 1,
                      fontSize: 11,
                      fontWeight: 700,
                      color: colors.textFaint,
                    }}
                  >
                    HUB
                  </span>
                  <span
                    style={{
                      minWidth: 64,
                      textAlign: 'right',
                      fontSize: 11,
                      fontWeight: 700,
                      color: colors.textFaint,
                    }}
                  >
                    TOTAL
                  </span>
                  <span
                    style={{
                      minWidth: 64,
                      textAlign: 'right',
                      fontSize: 11,
                      fontWeight: 700,
                      color: colors.textFaint,
                    }}
                  >
                    ACTIVE
                  </span>
                  <span
                    style={{
                      minWidth: 76,
                      textAlign: 'right',
                      fontSize: 11,
                      fontWeight: 700,
                      color: colors.textFaint,
                    }}
                  >
                    COMPLETED
                  </span>
                  <span
                    style={{
                      minWidth: 76,
                      textAlign: 'right',
                      fontSize: 11,
                      fontWeight: 700,
                      color: colors.textFaint,
                    }}
                  >
                    ABSENT %
                  </span>
                </div>
                {hubBreakdown.length === 0 ? (
                  <Typography
                    style={{
                      fontSize: 13,
                      color: colors.textDisabled,
                      textAlign: 'center',
                      padding: '20px 0',
                    }}
                  >
                    No data
                  </Typography>
                ) : (
                  hubBreakdown.map((h) => {
                    const ar =
                      h.total > 0 ? Math.round((h.active / h.total) * 100) : 0;
                    return (
                      <div key={h.hub} className={classes.tableRow}>
                        <span
                          className={classes.rowDot}
                          style={{ background: CORAL }}
                        />
                        <span className={classes.rowName}>{h.name}</span>
                        <span className={classes.rowNum}>{h.total}</span>
                        <span className={classes.rowGreen}>{h.active}</span>
                        <span
                          style={{
                            fontSize: 11,
                            color: colors.textFaint,
                            minWidth: 76,
                            textAlign: 'right',
                          }}
                        >
                          {h.completed}
                        </span>
                        <span
                          style={{
                            fontSize: 11,
                            color: CORAL,
                            fontWeight: 600,
                            minWidth: 76,
                            textAlign: 'right',
                          }}
                        >
                          {100 - ar}%
                        </span>
                      </div>
                    );
                  })
                )}
              </div>
            </div>
          </Grid>
          <Grid item xs={12} md={4}>
            <PaginatedListCard
              title="Active Students (status: Active)"
              items={activeStatusStudents}
              pageSize={8}
              emptyText="No students with Active status"
              renderItem={(s: any) => (
                <div>
                  <div style={{ fontSize: 12, fontWeight: 700, color: DARK }}>
                    {s.name}
                  </div>
                  <div
                    style={{
                      fontSize: 10,
                      color: colors.textFaint,
                      marginTop: 2,
                    }}
                  >
                    {s.hub} · {s.course || '—'}
                  </div>
                </div>
              )}
            />
          </Grid>
        </Grid>

        {hubBreakdown.length > 0 && (
          <Grid
            container
            spacing={2}
            alignItems="stretch"
            style={{ marginTop: 4 }}
          >
            <Grid item xs={12} md={8}>
              <ChartCard title="Enrolled vs Active — per hub">
                <ChartBox scrollCategoryCount={hubBreakdown.length}>
                  <Bar
                    data={{
                      labels: hubBreakdown.map((h) => h.name),
                      datasets: [
                        {
                          label: 'Enrolled',
                          data: hubBreakdown.map((h) => h.total),
                          backgroundColor: CORAL,
                          borderRadius: 4,
                        },
                        {
                          label: 'Active',
                          data: hubBreakdown.map((h) => h.active),
                          backgroundColor: GREEN,
                          borderRadius: 4,
                        },
                      ],
                    }}
                    options={{
                      ...barOpts,
                      legend: {
                        position: 'top',
                        labels: {
                          fontSize: 11,
                          padding: 14,
                          usePointStyle: true,
                        },
                      },
                    }}
                  />
                </ChartBox>
              </ChartCard>
            </Grid>
            <Grid item xs={12} md={4}>
              <ChartCard title="Share of Total Students">
                <DonutStat
                  items={hubBreakdown.map((h, i) => ({
                    label: h.name,
                    value: h.total,
                    color: CHART_PALETTE[i % CHART_PALETTE.length],
                  }))}
                />
              </ChartCard>
            </Grid>
          </Grid>
        )}

        {/* ── Course Breakdown ──────────────────────────────────────────── */}
        <div className={classes.sectionLabel}>Course Breakdown</div>
        <Grid container spacing={2} alignItems="stretch">
          <Grid item xs={12} md={5}>
            <div
              className={classes.card}
              style={{ height: '100%', boxSizing: 'border-box' }}
            >
              <div className={classes.cardTableInner}>
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 10,
                    padding: '10px 0 6px',
                    borderBottom: `2px solid ${colors.border}`,
                  }}
                >
                  <span
                    style={{
                      flex: 1,
                      fontSize: 11,
                      fontWeight: 700,
                      color: colors.textFaint,
                    }}
                  >
                    COURSE
                  </span>
                  <span
                    style={{
                      minWidth: 64,
                      textAlign: 'right',
                      fontSize: 11,
                      fontWeight: 700,
                      color: colors.textFaint,
                    }}
                  >
                    ENROLLED
                  </span>
                  <span
                    style={{
                      minWidth: 64,
                      textAlign: 'right',
                      fontSize: 11,
                      fontWeight: 700,
                      color: colors.textFaint,
                    }}
                  >
                    ACTIVE
                  </span>
                  <span
                    style={{
                      minWidth: 84,
                      textAlign: 'right',
                      fontSize: 11,
                      fontWeight: 700,
                      color: colors.textFaint,
                    }}
                  >
                    COMPLETED
                  </span>
                  <span
                    style={{
                      minWidth: 84,
                      textAlign: 'right',
                      fontSize: 11,
                      fontWeight: 700,
                      color: colors.textFaint,
                    }}
                  >
                    COMPLETION%
                  </span>
                </div>
                {courseBreakdown.length === 0 ? (
                  <Typography
                    style={{
                      fontSize: 13,
                      color: colors.textDisabled,
                      textAlign: 'center',
                      padding: '20px 0',
                    }}
                  >
                    No data
                  </Typography>
                ) : (
                  courseBreakdown.slice(0, 10).map((c, i) => {
                    const cr =
                      c.total > 0
                        ? Math.round((c.completed / c.total) * 100)
                        : 0;
                    return (
                      <div key={c.course || i} className={classes.tableRow}>
                        <span className={classes.rowName}>{c.course}</span>
                        <span className={classes.rowNum}>{c.total}</span>
                        <span className={classes.rowGreen}>{c.active}</span>
                        <span
                          style={{
                            fontSize: 11,
                            color: colors.textFaint,
                            minWidth: 84,
                            textAlign: 'right',
                          }}
                        >
                          {c.completed}
                        </span>
                        <span
                          style={{
                            minWidth: 84,
                            textAlign: 'right',
                            fontSize: 12,
                            fontWeight: 700,
                            color: cr >= 50 ? GREEN : AMBER,
                          }}
                        >
                          {cr}%
                        </span>
                      </div>
                    );
                  })
                )}
              </div>
            </div>
          </Grid>
          {courseBreakdown.length > 0 && (
            <Grid item xs={12} md={7}>
              <ChartCard title="Enrolled vs Active — per course">
                <ChartBox
                  height={340}
                  scrollCategoryCount={courseBreakdown.length}
                >
                  <Bar
                    data={{
                      labels: courseBreakdown.map((c) => c.course),
                      datasets: [
                        {
                          label: 'Enrolled',
                          data: courseBreakdown.map((c) => c.total),
                          backgroundColor: CORAL,
                          borderRadius: 4,
                          barPercentage: 0.5,
                          categoryPercentage: 0.6,
                        },
                        {
                          label: 'Active',
                          data: courseBreakdown.map((c) => c.active),
                          backgroundColor: GREEN,
                          borderRadius: 4,
                          barPercentage: 0.5,
                          categoryPercentage: 0.6,
                        },
                      ],
                    }}
                    options={{
                      ...barOpts,
                      legend: {
                        position: 'top',
                        labels: {
                          fontSize: 11,
                          padding: 14,
                          usePointStyle: true,
                        },
                      },
                    }}
                  />
                </ChartBox>
              </ChartCard>
            </Grid>
          )}
        </Grid>

        <div style={{ height: 32 }} />
      </div>
    </Layout>
  );
};

export default ReportList;
